import React from "react";
import { Card, Tile } from "../shared";
import * as m from "../../lib/model";
import { AlarmClock, CalendarCheck, TrendingUp } from "lucide-react";

export const STAGES = ["Prospect", "Emailed", "Intro Call", "Diligence", "Soft Circle", "Committed", "Funded", "Passed"];
const FUNNEL = STAGES.filter((s) => s !== "Passed");

const closeTile = (tl, onOpen) => {
  if (!tl) return { value: "—", sub: "set a target close in Raise Timeline" };
  const d = tl.daysToClose;
  const close = new Date(tl.closeDate + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return { value: d < 0 ? `${-d}d past` : `${d} days`, sub: `close ${close} · week ${Math.min(tl.weeksElapsed || 1, tl.weekCount)} of ${tl.weekCount}`, tone: d < 0 ? "pass" : undefined };
};

export const RaiseTiles = ({ s, tl, onOpenTimeline }) => {
  const pct = Math.min(100, Math.round(s.pct || 0));
  const ct = closeTile(tl);
  return (
    <>
      <div className="cm-tiles" data-testid="cm-scoreboard">
        <Tile label="Raise target" value={s.target ? m.fmt$(s.target) : "—"} sub={`${s.count} LPs tracked`} testid="cm-tile-target" />
        <div onClick={onOpenTimeline} className="cm-tile-link" role="button" data-testid="cm-tile-close-wrap">
          <Tile label="Days to close" value={ct.value} sub={ct.sub} tone={ct.tone} testid="cm-tile-close" />
        </div>
        <Tile label="Committed + funded" value={m.fmt$(s.closed)} sub={s.target ? `${pct}% of target` : "across all deals"} tone={s.target && pct >= 100 ? "go" : s.target && pct >= 50 ? "amber" : undefined} testid="cm-tile-closed" />
        <Tile label="Remaining to close" value={s.target ? m.fmt$(s.remaining) : "—"} sub={s.target ? "after room commits" : "pick a deal to see the gap"} testid="cm-tile-remaining" />
        <Tile label="Weighted pipeline" value={m.fmt$(s.weighted)} sub="probability-adjusted" testid="cm-tile-weighted" />
        <Tile label="Average ticket" value={m.fmt$(s.avgTicket)} sub={`${s.liveCount} live conversations`} testid="cm-tile-avg" />
        <Tile label="Soft-circled" value={m.fmt$(s.softCircled)} sub="verbal, not signed" testid="cm-tile-soft" />
        <Tile label="Plan progress" value={tl ? `${tl.tasksDone}/${tl.tasksTotal}` : "—"} sub={tl ? "weekly activities done" : "no timeline yet"} testid="cm-tile-tasks" />
      </div>
      {s.target > 0 && (
        <div className="cm-progress" data-testid="cm-progress">
          <div className="cm-bar"><span style={{ width: `${pct}%` }} /></div>
          <div className="cm-bar-legend"><span>{m.fmt$(s.closed)} raised</span><b>{pct}%</b><span>{m.fmt$(s.target)} target</span></div>
        </div>
      )}
    </>
  );
};

const realMoney = (l) => Number(l.funded || 0) || Number(l.committed || 0) || Number(l.indicated || 0) || 0;
const isCommitted = (l) => Number(l.committed || 0) > 0 || Number(l.funded || 0) > 0;
const fmtDay = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

// Simpler, activity-first view for section 1 (replaces the busy scoreboard tiles).
export const RaiseActivity = ({ s = {}, tl, leads = [], onOpenTimeline }) => {
  const pct = Math.min(100, Math.round(s.pct || 0));
  const commits = leads.filter(isCommitted).sort((a, b) => realMoney(b) - realMoney(a));
  const softs = leads.filter((l) => l.stage === "Soft Circle" && !isCommitted(l)).sort((a, b) => realMoney(b) - realMoney(a));
  const committedSoft = [...commits, ...softs];
  const engaged = leads
    .filter((l) => l.stage !== "Passed")
    .sort((a, b) => (b.lastContact || b.updatedAt || "").localeCompare(a.lastContact || a.updatedAt || ""))
    .slice(0, 8);
  return (
    <div data-testid="cm-raise-activity">
      <div className="cm-tiles cm-tiles-3">
        <Tile label="Raised" value={m.fmt$(s.closed || 0)} sub={s.target ? `${pct}% of ${m.fmt$(s.target)} target` : "no target set"}
          tone={s.target && pct >= 100 ? "go" : s.target && pct >= 50 ? "amber" : undefined} testid="ra-raised" />
        <Tile label="Commitments" value={String(commits.length)} sub={softs.length ? `+ ${softs.length} soft-circled` : "signed or funded"} testid="ra-commits" />
        <Tile label="Live conversations" value={String(s.liveCount || 0)} sub={`${s.count || 0} LPs tracked`} testid="ra-live" />
      </div>
      {s.target > 0 && (
        <div className="cm-progress" data-testid="ra-progress">
          <div className="cm-bar"><span style={{ width: `${pct}%` }} /></div>
          <div className="cm-bar-legend"><span>{m.fmt$(s.closed || 0)} committed + funded</span><b>{pct}%</b><span>{m.fmt$(s.target)} target</span></div>
        </div>
      )}
      <div onClick={onOpenTimeline} role="button" className="cm-tile-link ra-timeline" data-testid="ra-timeline">
        {tl
          ? <span>{tl.daysToClose < 0 ? `${-tl.daysToClose}d past target close` : `${tl.daysToClose}d to target close`} · week {Math.min(tl.weeksElapsed || 1, tl.weekCount)}/{tl.weekCount} · {tl.tasksDone}/{tl.tasksTotal} activities done</span>
          : <span>No raise timeline yet — click to set a target close and generate the weekly plan.</span>}
      </div>
      <div className="cm-activity-grid">
        <Card title={`Commitments & soft circles — ${committedSoft.length}`} testid="ra-commit-list">
          {committedSoft.length === 0 && <div className="cm-empty">No commitments yet. As LPs soft-circle or sign, they show up here with their amounts.</div>}
          {committedSoft.map((l) => (
            <div key={l.id} className="cm-src-row" data-testid={`ra-commit-${l.id}`}>
              <span>{l.name || l.firm || "—"}{l.firm && l.name ? ` · ${l.firm}` : ""}</span>
              <span className="tag">{l.stage}</span>
              <b>{m.fmt$(realMoney(l))}</b>
            </div>
          ))}
        </Card>
        <Card title="Recently engaged" testid="ra-engaged-list">
          {engaged.length === 0 && <div className="cm-empty">No LP activity yet — add LPs in the pipeline or log a touch.</div>}
          {engaged.map((l) => (
            <div key={l.id} className="cm-src-row" data-testid={`ra-engaged-${l.id}`}>
              <span>{l.name || l.firm || "—"}</span>
              <span className="tag">{l.stage}</span>
              <b className="ra-date">{fmtDay(l.lastContact)}</b>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};


export const FunnelCard = ({ s }) => {
  const maxN = Math.max(1, ...FUNNEL.map((k) => (s.byStage?.[k]?.n || 0)));
  return (
    <Card title="Conversion funnel" testid="cm-funnel">
      {FUNNEL.map((k) => {
        const row = s.byStage?.[k] || { n: 0, amount: 0 };
        return (
          <div key={k} className="cm-funnel-row" data-testid={`cm-funnel-${k.replace(/\s/g, "-").toLowerCase()}`}>
            <span className="cf-lbl">{k}</span>
            <div className="cf-track"><span style={{ width: `${(row.n / maxN) * 100}%` }} /></div>
            <span className="cf-n">{row.n}</span>
            <span className="cf-amt">{m.fmt$(row.amount)}</span>
          </div>
        );
      })}
      <div className="cm-funnel-row muted">
        <span className="cf-lbl">Passed</span><div className="cf-track" /><span className="cf-n">{s.byStage?.Passed?.n || 0}</span><span className="cf-amt">—</span>
      </div>
    </Card>
  );
};

export const FollowUpsCard = ({ s, onJump }) => {
  const fu = s.followUps || {};
  return (
    <Card title="Follow-ups due" testid="cm-followups">
      <div className="cm-fu">
        <button className={`cm-fu-box ${fu.overdue ? "hot" : ""}`} onClick={() => onJump("overdue")} data-testid="cm-fu-overdue">
          <AlarmClock size={14} /><b>{fu.overdue || 0}</b><span>Overdue</span>
        </button>
        <button className="cm-fu-box" onClick={() => onJump("today")} data-testid="cm-fu-today">
          <CalendarCheck size={14} /><b>{fu.today || 0}</b><span>Today</span>
        </button>
        <button className="cm-fu-box" onClick={() => onJump("week")} data-testid="cm-fu-week">
          <TrendingUp size={14} /><b>{fu.week || 0}</b><span>Next 7 days</span>
        </button>
      </div>
      <div className="cm-empty" style={{ marginTop: 10 }}>Click a box to filter the LP pipeline to those follow-ups.</div>
    </Card>
  );
};

export const ReadinessCard = ({ s }) => {
  const rd = s.readiness || {};
  return (
    <Card title="Close readiness" testid="cm-readiness">
      <div className="cm-rd">
        <div><span>Committed, docs outstanding</span><b className={rd.needDocs ? "warn" : ""}>{rd.needDocs || 0}</b></div>
        <div><span>Signed, awaiting wire</span><b className={rd.awaitingWire ? "warn" : ""}>{rd.awaitingWire || 0}</b></div>
        <div><span>Accreditation unverified</span><b className={rd.unverified ? "warn" : ""}>{rd.unverified || 0}</b></div>
        <div><span>Quiet 21+ days, no follow-up set</span><b className={rd.stale ? "warn" : ""}>{rd.stale || 0}</b></div>
      </div>
    </Card>
  );
};

export const SourcesCard = ({ s }) => (
  <Card title="Top sources driving commitments" testid="cm-sources">
    {(s.sources || []).length === 0 && <div className="cm-empty">No sources logged yet — add a source or referrer on each LP.</div>}
    {(s.sources || []).map((r) => (
      <div key={r.source} className="cm-src-row">
        <span>{r.source}</span><span className="cs-n">{r.n} LP{r.n === 1 ? "" : "s"}</span><b>{m.fmt$(r.amount)}</b>
      </div>
    ))}
  </Card>
);
